import React, { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Phone, CheckCircle2, ChevronLeft, Shield, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { FAQ } from "../components/FAQ";
import { FAQS } from "../data/faqs";
import { getServiceBySlug, SERVICE_PAGES } from "../data/services";

const PHONE_URL = "tel:+16094512225";
const PHONE_FORMATTED = "(609) 451-2225";

const handlePhoneClick = (e) => {
  e.preventDefault();
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-17526247982/6ELDCNWcmu0bEK6klaVB",
      value: 1.0,
      currency: "USD",
      event_callback: function () {
        window.location.href = PHONE_URL;
      },
    });
    setTimeout(() => {
      window.location.href = PHONE_URL;
    }, 1000);
  } else {
    window.location.href = PHONE_URL;
  }
};

export const ServicePage = ({ HeaderComponent, FooterComponent, MobileBarComponent }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Service not found</h1>
          <Link to="/" className="text-orange-500 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const path = `/${service.slug}`;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `${service.name} Repair`,
    provider: {
      "@type": "LocalBusiness",
      name: "Major Appliance Repair NJ",
      telephone: "+1-609-451-2225",
      address: {
        "@type": "PostalAddress",
        streetAddress: "1099 White Horse Rd 2nd Floor",
        addressLocality: "Voorhees Township",
        addressRegion: "NJ",
        postalCode: "08043",
        addressCountry: "US",
      },
    },
    areaServed: ["Gloucester County NJ", "Camden County NJ", "Burlington County NJ"],
    description: service.metaDescription,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={service.title}
        description={service.metaDescription}
        path={path}
        schema={[serviceSchema, faqSchema]}
      />
      <HeaderComponent />

      <main>
        {/* Hero */}
        <section className="bg-white py-12 md:py-20" data-testid="service-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-gray-500 hover:text-orange-500 mb-6 text-sm"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Back to home
            </button>

            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {service.h1}
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl">{service.intro}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`/book/${service.id}`}>
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg font-semibold w-full sm:w-auto shadow-lg shadow-orange-500/25"
                  data-testid="service-book-btn"
                >
                  Book Online & Save 5%
                </Button>
              </Link>
              <a
                href={PHONE_URL}
                onClick={handlePhoneClick}
                className="inline-flex items-center justify-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-full px-8 py-4 text-lg font-semibold transition-all"
                data-testid="service-phone-btn"
              >
                <Phone className="w-5 h-5" /> Or call {PHONE_FORMATTED}
              </a>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              <div className="flex items-center gap-3 text-gray-700">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-sm">Same-day or next-day service</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Shield className="w-5 h-5 text-orange-500" />
                <span className="text-sm">30-day parts and labor warranty</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-orange-500" />
                <span className="text-sm">Licensed and insured</span>
              </div>
            </div>
          </div>
        </section>

        {/* Common Issues */}
        <section className="bg-gray-50 py-16 border-y border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Common {service.name} Problems We Fix
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {service.commonIssues.map((issue, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4"
                >
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Brands */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Brands We Service
            </h2>
            <p className="text-gray-600 mb-6">
              {service.why}
            </p>
            <div className="flex flex-wrap gap-3">
              {service.brands.map((b) => (
                <span
                  key={b}
                  className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="bg-gray-50 py-16 border-y border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              South Jersey {service.name} Repair Areas
            </h2>
            <p className="text-gray-600 mb-6">
              We provide {service.name.toLowerCase()} repair across Gloucester, Camden, and North Burlington Counties.
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                "Voorhees",
                "Cherry Hill",
                "Sicklerville",
                "Mount Laurel",
                "Marlton",
                "Sewell",
                "Washington Township",
                "Wenonah",
                "Woodbury",
              ].map((city) => (
                <Link
                  key={city}
                  to={`/appliance-repair-${city.toLowerCase().replace(/ /g, "-")}-nj`}
                  className="bg-white border border-gray-200 hover:border-orange-300 text-gray-700 hover:text-orange-500 px-4 py-2 rounded-full text-sm"
                >
                  {city}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Other Appliances We Repair
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {SERVICE_PAGES.filter((s) => s.id !== service.id).map((s) => (
                <Link
                  key={s.id}
                  to={`/${s.slug}`}
                  className="bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-300 rounded-xl p-6 text-center transition-all"
                >
                  <h3 className="font-semibold text-gray-800 hover:text-orange-500">
                    {s.name} Repair
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <FAQ />
      </main>

      <FooterComponent />
      {MobileBarComponent && <MobileBarComponent />}
    </div>
  );
};

export default ServicePage;
